import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'

const PHONE_REGEX = /^[0-9 +()-]{6,}$/

const buildInitialValues = () => ({
  civilite_amical: '',
  matricule_fiscal_amical: '',
  telephone_amical: '',
})

export const useAmicalForm = () => {
  const { t } = useTranslation()
  const [values, setValues] = useState(buildInitialValues)
  const [errors, setErrors] = useState({})

  const setField = useCallback((field, value) => {
    setValues((prev) => ({ ...prev, [field]: value }))
    setErrors((prev) => {
      if (!prev[field]) return prev
      const { [field]: _omit, ...rest } = prev
      return rest
    })
  }, [])

  const setGuestField = useCallback((guestKey, field, value) => {
    setValues((prev) => ({
      ...prev,
      [guestKey]: { ...(prev[guestKey] || {}), [field]: value },
    }))
    setErrors((prev) => {
      if (!prev[guestKey]?.[field]) return prev
      const guestErrors = { ...prev[guestKey] }
      delete guestErrors[field]
      const next = { ...prev }
      if (Object.keys(guestErrors).length === 0) delete next[guestKey]
      else next[guestKey] = guestErrors
      return next
    })
  }, [])

  const validateAdherent = useCallback(() => {
    const next = {}
    if (!values.civilite_amical) {
      next.civilite_amical = t('moteurReservation.amical.civilityRequired')
    }
    if (!values.matricule_fiscal_amical?.trim()) {
      next.matricule_fiscal_amical = t('moteurReservation.amical.matriculeRequired')
    }
    if (!values.telephone_amical?.trim()) {
      next.telephone_amical = t('moteurReservation.amical.phoneRequired')
    } else if (!PHONE_REGEX.test(values.telephone_amical.trim())) {
      next.telephone_amical = t('moteurReservation.amical.invalidPhone')
    }
    setErrors((prev) => ({ ...prev, ...next }))
    return Object.keys(next).length === 0
  }, [values, t])

  const validateGuestsIdentity = useCallback(
    (roomsConfig = []) => {
      const next = {}
      roomsConfig.forEach((room, idx) => {
        const total = (room.adults || 0) + (room.children || 0)
        for (let i = 0; i < total; i++) {
          if (i !== 0) continue
          const key = `room_${idx}_guest_${i}`
          const g = values[key] || {}
          const guestErr = {}
          if (!g.last_name?.trim()) {
            guestErr.last_name = t('moteurReservation.amical.lastNameRequired')
          }
          if (!g.first_name?.trim()) {
            guestErr.first_name = t('moteurReservation.amical.firstNameRequired')
          }
          if (Object.keys(guestErr).length) next[key] = guestErr
        }
      })
      setErrors((prev) => ({ ...prev, ...next }))
      return Object.keys(next).length === 0
    },
    [values, t]
  )

  const validate = useCallback(
    (roomsConfig = []) =>
      validateAdherent() && validateGuestsIdentity(roomsConfig),
    [validateAdherent, validateGuestsIdentity]
  )

  const reset = useCallback(() => {
    setValues(buildInitialValues())
    setErrors({})
  }, [])

  return {
    values,
    errors,
    setField,
    setGuestField,
    validateAdherent,
    validateGuestsIdentity,
    validate,
    reset,
  }
}

export default useAmicalForm
